import { format as formatCnpjLib } from '@fnando/cnpj';
import { normalizarDocumento } from './documento';

function stringToNumber(data) {
  if (typeof data === typeof 'string') {
    const value = data.replace(/[^0-9,]/g, '').replace(',', '.');
    return parseFloat(value);
  }
  return data;
}

function tamanhoArquivo(bytes) {
  const thresh = 1000;
  if (Math.abs(bytes) < thresh) return `${bytes} B`;

  const units = ['kB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
  let u = -1;
  let bytesRestantes = bytes;
  do {
    bytesRestantes /= thresh;
    u += 1;
  } while (Math.abs(bytesRestantes) >= thresh && u < units.length - 1);
  return `${bytesRestantes.toFixed(1)} ${units[u]}`;
}

function formatarData(data) {
  if (!data || data === '') return '';
  return `${new Date(data).toLocaleDateString('pt-BR')} ${new Date(data).toLocaleTimeString(
    'pt-BR',
    { hour: '2-digit', minute: '2-digit' },
  )}`;
}

function formatarDataSemHorario(data) {
  if (!data || data === '') return '';

  const date = data instanceof Date ? data : new Date(data);
  if (Number.isNaN(date.getTime())) return '';

  return date.toLocaleDateString('pt-BR');
}

/** Converte `YYYY-MM-DD` ou `Date` em data local à meia-noite (sem deslocamento UTC).
 * Uso previsto: fluxo ARP (`views/Pregao/Arp/helpers.js`); não substitui `formatarData*` do legado.
 */
function parseIsoDateLocal(value) {
  if (value == null) return null;

  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    const dt = new Date(value.getTime());
    dt.setHours(0, 0, 0, 0);
    return dt;
  }

  const s = String(value).trim();
  if (!s) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]) - 1;
  const d = Number(m[3]);
  const dt = new Date(y, mo, d);
  if (dt.getFullYear() !== y || dt.getMonth() !== mo || dt.getDate() !== d) return null;
  dt.setHours(0, 0, 0, 0);
  return dt;
}

/** Valor para `FormData` / API (`YYYY-MM-DD`) a partir de Date ou string de formulário. */
function dateToFormIsoString(value) {
  const d = parseIsoDateLocal(value);
  if (!d) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Soma meses em data local, normalizada à meia-noite. Uso previsto: fluxo ARP.
 * Preserva o dia quando existir no mês alvo; senão usa o último dia desse mês
 * (ex.: 31/01 + 1 mês → 28/02, não 03/03 como com setMonth).
 */
function addMonthsSafe(date, months) {
  const year = date.getFullYear();
  const monthIndex = date.getMonth();
  const day = date.getDate();
  const targetMonthIndex = monthIndex + months;
  const targetYear = year + Math.floor(targetMonthIndex / 12);
  const normalizedMonth = ((targetMonthIndex % 12) + 12) % 12;
  const lastDayOfTargetMonth = new Date(targetYear, normalizedMonth + 1, 0).getDate();
  const targetDay = Math.min(day, lastDayOfTargetMonth);
  const d = new Date(targetYear, normalizedMonth, targetDay);
  d.setHours(0, 0, 0, 0);
  return d;
}

/** Conta dias úteis (seg–sex) entre duas datas, exclusivo do início. Uso previsto: fluxo ARP. */
function businessDaysBetween(startDate, endDate) {
  const start = new Date(startDate.getTime());
  const end = new Date(endDate.getTime());
  start.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);
  if (end.getTime() <= start.getTime()) return 0;
  let days = 0;
  const d = new Date(start.getTime());
  d.setDate(d.getDate() + 1);
  while (d.getTime() <= end.getTime()) {
    const wd = d.getDay();
    if (wd !== 0 && wd !== 6) days += 1;
    d.setDate(d.getDate() + 1);
  }
  return days;
}

/** Exibe data em pt-BR a partir de valor típico de formulário (`YYYY-MM-DD` do `input type="date"`).
 * Evita deslocamento de dia por fuso ao não usar `Date` para strings só-data.
 * Para timestamps / ISO com hora, prefira `formatarData` ou `formatarDataSemHorario`.
 */
function formatarDataFormularioIso(data) {
  if (!data || data === '') return '';
  const v = String(data).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(v)) {
    const yyyy = v.slice(0, 4);
    const mm = v.slice(5, 7);
    const dd = v.slice(8, 10);
    return `${dd}/${mm}/${yyyy}`;
  }
  if (/^\d{2}\/\d{2}\/\d{4}/.test(v)) return v;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return v;
  return d.toLocaleDateString('pt-BR');
}

function formatarDataSegundo(data) {
  if (!data || data === '') return '';
  return `${new Date(data).toLocaleDateString('pt-BR')} ${new Date(data).toLocaleTimeString(
    'pt-BR',
    {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    },
  )}`;
}

function setTipoArquivo(nomeArquivo) {
  const extensao = nomeArquivo.split('.');
  return extensao[extensao.length - 1].toUpperCase();
}

function formatarValor(valor, minimoCasasDecimais = 4) {
  return `${(valor ?? 0).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: minimoCasasDecimais,
    maximumFractionDigits: 4,
  })}`;
}

function formatarValorSemPrefixo(valor, minimoCasasDecimais = 4) {
  return `${valor.toLocaleString('pt-BR', {
    minimumFractionDigits: minimoCasasDecimais,
    maximumFractionDigits: 4,
  })}`;
}

function removerDecimaisZerados(valorFormatado) {
  const texto = String(valorFormatado || '');
  const indiceDecimal = texto.indexOf(',');
  if (indiceDecimal < 0) return texto;

  const decimais = texto.slice(indiceDecimal + 1);
  if (!decimais || !/^0+$/.test(decimais)) return texto;

  return texto.slice(0, indiceDecimal);
}

function formatarPorcentagem(valor) {
  return `${valor.toLocaleString('pt-BR', {
    style: 'decimal',
    minimumFractionDigits: 4,
    maximumFractionDigits: 4,
  })}`;
}

function formatarPadrao(valor, padrao) {
  return valor || padrao;
}

function formatarNumero(numero) {
  return `${numero.toLocaleString('pt-BR', { style: 'decimal' })}`;
}

function formatarCnpj(valor) {
  // CNPJ alfanumérico: os 12 primeiros caracteres podem ser letras, então a
  // regex `\d`-only antiga falhava silenciosamente (retornava sem máscara).
  // `@fnando/cnpj` sabe formatar tanto o CNPJ numérico legado quanto o novo.
  const clean = normalizarDocumento(valor);
  if (clean.length !== 14) return valor;
  return formatCnpjLib(clean);
}

// Formata CPF (11 dígitos, sempre numérico) ou CNPJ (14 caracteres,
// alfanumérico nos 12 primeiros) a partir de um documento "sujo" (com ou sem
// pontuação). Usada para consolidar as implementações antes duplicadas em
// SellerData.vue, CompanyDataRegister.vue e ViewCompanyData.vue.
function formatarDocumento(valor) {
  if (!valor) return '';
  const raw = String(valor);
  const clean = normalizarDocumento(raw);

  if (clean.length === 11 && /^\d{11}$/.test(clean)) {
    return clean.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3‑$4');
  }

  if (clean.length === 14) {
    return formatCnpjLib(clean).replace('-', '‑');
  }

  return raw;
}

function tituloEstadoItem(estado, modoDisputa = 'ABERTO') {
  switch (estado) {
    case 'CADASTRADA':
      return 'Cadastrado';
    case 'LANCE-ENCERRADA':
      if (modoDisputa === 'ABERTO' || modoDisputa === 'FECHADO_ABERTO') {
        return 'Aguardando';
      }
      return 'Finalizado';
    case 'LANCE':
      return 'Iniciado';
    case 'LANCE-RANDOMICO':
      return 'Iniciado';
    case 'LANCE-FECHADO':
      return 'Iniciado';
    case 'PRIMEIRA-CHAMADA':
      return '1ª Chamada';
    case 'NOVA-CHAMADA':
      return 'Nova Chamada';
    case 'PREFERENCIA':
      return 'Preferência';
    case 'PREFERENCIA-INICIADA':
      return 'Iniciada';
    case 'NEGOCIACAO':
      return 'Negociação';
    case 'NEGOCIACAO-ENCERRADA':
      return 'Encerrada';
    case 'JULGAMENTO':
      return 'Julgamento';
    case 'JULGAMENTO-ENCERRADO':
      return 'Encerrado';
    case 'HABILITACAO':
      return 'Habilitação';
    case 'HABILITACAO-ENCERRADA':
      return 'Habilitado';
    case 'ADESAO':
      return 'Adesão';
    case 'ADJUDICADO':
      return 'Adjudicado';
    case 'HOMOLOGADO':
      return 'Homologado';
    case 'FRACASSADA':
      return 'Fracassado';
    case 'DESERTO':
      return 'Deserto';
    case 'REVOGADO':
      return 'Revogado';
    case 'ANULADO':
      return 'Anulado';
    default:
      return 'Finalizado';
  }
}

function tituloEstadoItemDispensa(estado) {
  switch (estado) {
    case 'CADASTRADO':
      return 'Cadastrado';
    case 'LANCE':
      return 'Iniciado';
    case 'RATIFICADO':
      return 'Ratificado';
    case 'FRACASSADO':
      return 'Fracassado';
    case 'DESERTO':
      return 'Deserto';
    case 'REVOGADO':
      return 'Revogado';
    case 'ANULADO':
      return 'Anulado';
    default:
      return 'Finalizado';
  }
}

function estadoItem(estado, modoDisputa = 'ABERTO') {
  if (
    [
      'CADASTRADA',
      'PRIMEIRA-CHAMADA',
      'NOVA-CHAMADA',
      'NEGOCIACAO',
      'JULGAMENTO',
      'HABILITACAO',
      'ADESAO',
    ].indexOf(estado) > -1 ||
    (estado === 'LANCE-ENCERRADA' && (modoDisputa === 'ABERTO' || modoDisputa === 'FECHADO_ABERTO'))
  ) {
    return '';
  }
  if (['FRACASSADA', 'DESERTO', 'REVOGADO', 'ANULADO'].indexOf(estado) > -1) return 'is-danger';
  return 'is-success';
}

function estadoItemDispensa(estado) {
  if (estado === 'CADASTRADO') {
    return '';
  }
  if (['FRACASSADO', 'DESERTO', 'REVOGADO'].indexOf(estado) > -1) return 'is-danger';
  return 'is-success';
}

function formatType(type) {
  return {
    MATERIAL: 'MATERIAL',
    SERVICE: 'SERVIÇO',
    LOT: 'LOTE',
  }[type];
}

function truncate(text, length) {
  return text.length > length ? `${text.slice(0, length)}...` : text;
}

export {
  stringToNumber,
  tamanhoArquivo,
  formatarData,
  formatarDataSemHorario,
  parseIsoDateLocal,
  dateToFormIsoString,
  addMonthsSafe,
  businessDaysBetween,
  formatarDataFormularioIso,
  formatarDataSegundo,
  formatarValor,
  formatarValorSemPrefixo,
  removerDecimaisZerados,
  formatarNumero,
  formatarPorcentagem,
  setTipoArquivo,
  tituloEstadoItem,
  tituloEstadoItemDispensa,
  estadoItem,
  estadoItemDispensa,
  formatarPadrao,
  formatarCnpj,
  formatarDocumento,
  formatType,
  truncate,
};
